import React, {useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from "react-intl";
import {HttpStatus} from "../../../../../modules/common/enums/HttpStatus";
import {MailPropertiesDto} from "../../../../../modules/mail/dto/MailPropertiesDto";
import {MailPropertiesApi} from "../../../../../modules/mail/api/MailPropertiesApi";
import {MessageUtils} from "../../../../../utils/MessageUtils";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@mui/material";
import FormFieldText from "../../../../../components/FormFieldText";
import CancelButton from "../../../../../components/buttons/CancelButton";
import SaveButton from "../../../../../components/buttons/SaveButton";
import FormFieldSwitch from "../../../../../components/FormFieldSwitch";

export type MailPropertiesDetailDialogProps = {
    open: boolean;
    mailProperties: MailPropertiesDto;
    onClose: () => void;
    onOk: () => void;
};

const MailPropertiesDetailDialog: React.FC<MailPropertiesDetailDialogProps> = (props) => {
    const intl = useIntl();
    const title = props.mailProperties.id ? 'MAIL_PROPERTIES_UPDATE' : 'MAIL_PROPERTIES_CREATE';

    const [innerMailProperties, setInnerMailProperties] = useState<MailPropertiesDto>(props.mailProperties);

    const [messageMap, setMessageMap] = useState(new Map<string, string>());

    useEffect(() => {
        if (props.open) {
            setInnerMailProperties({...props.mailProperties});
        }
    }, [props.open]);

    const handleChange = (name: string, value: any) => {
        setInnerMailProperties(previousInnerMailProperties => ({...previousInnerMailProperties, [name]: value}));
    };

    const handleOk = async () => {
        if (validate()) {
            if (props.mailProperties.id) {
                const responseResult = await MailPropertiesApi.updateMailProperties(innerMailProperties);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'MAIL_PROPERTIES_UPDATE_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            } else {
                const responseResult = await MailPropertiesApi.createMailProperties(innerMailProperties);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'MAIL_PROPERTIES_CREATE_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            }
        }
    };

    const handleCancel = () => {
        props.onClose();
    };

    const validate = () => {
        const newMessageMap = new Map<string, string>();
        if (innerMailProperties.username === '') {
            newMessageMap.set('username', intl.formatMessage({id: 'MAIL_PROPERTIES_USERNAME_REQUIRED'}));
        }
        if (innerMailProperties.mailHost === '') {
            newMessageMap.set('mailHost', intl.formatMessage({id: 'MAIL_PROPERTIES_MAIL_HOST_REQUIRED'}));
        }
        if (innerMailProperties.mailFrom === '') {
            newMessageMap.set('mailFrom', intl.formatMessage({id: 'MAIL_PROPERTIES_MAIL_FROM_REQUIRED'}));
        }
        if (!innerMailProperties.mailPort) {
            newMessageMap.set('mailPort', intl.formatMessage({id: 'MAIL_PROPERTIES_MAIL_PORT_REQUIRED'}));
        }

        setMessageMap(newMessageMap);
        return newMessageMap.size <= 0;
    };

    return (
        <Dialog open={props.open} onClose={handleCancel} fullWidth={true}>
            <DialogTitle>
                {intl.formatMessage({id: title})}
            </DialogTitle>
            <DialogContent>
                <FormFieldText label={intl.formatMessage({id: "MAIL_PROPERTIES_USERNAME"})}
                               required={true} errorText={messageMap.get("username")}
                               value={innerMailProperties.username}
                               onChange={(value) => {
                                   handleChange("username", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "MAIL_PROPERTIES_PASSWORD"})}
                               value={innerMailProperties.password}
                               onChange={(value) => {
                                   handleChange("password", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "MAIL_PROPERTIES_MAIL_HOST"})}
                               required={true} errorText={messageMap.get("mailHost")}
                               value={innerMailProperties.mailHost}
                               onChange={(value) => {
                                   handleChange("mailHost", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "MAIL_PROPERTIES_MAIL_FROM"})}
                               required={true} errorText={messageMap.get("mailFrom")}
                               value={innerMailProperties.mailFrom}
                               onChange={(value) => {
                                   handleChange("mailFrom", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "MAIL_PROPERTIES_MAIL_PORT"})}
                               required={true} errorText={messageMap.get("mailPort")}
                               type="number"
                               value={innerMailProperties.mailPort}
                               onChange={(value) => {
                                   handleChange("mailPort", value)
                               }}/>
                <FormFieldSwitch label={intl.formatMessage({id: "MAIL_PROPERTIES_SMTP_AUTH"})}
                                 checked={innerMailProperties.smtpAuth === 1}
                                 onChange={(value) => {
                                     handleChange("smtpAuth", value ? 1 : 0)
                                 }}/>
                <FormFieldSwitch label={intl.formatMessage({id: "MAIL_PROPERTIES_SMTP_SSL"})}
                                 checked={innerMailProperties.smtpSsl === 1}
                                 onChange={(value) => {
                                     handleChange("smtpSsl", value ? 1 : 0)
                                 }}/>
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={handleCancel}/>
                <SaveButton onClick={handleOk}/>
            </DialogActions>
        </Dialog>
    );
};

export default MailPropertiesDetailDialog;
